import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
export default {
	init() {
		const sections = document.querySelectorAll('.layout.accordions');
		if (!sections.length) return;

		function initSection(section) {
			let init = false;
			let swiper;
			let openAllRaf = 0;
			const accordionDetails = section.querySelectorAll('.accordion-item');
			const accordionSwiper = section.querySelector('.accordion-swiper');
			const imageColumn = section.querySelector('.image-column');
			const imageEls = imageColumn
				? Array.from(imageColumn.querySelectorAll('img[data-image-key]'))
				: [];
			const fallbackImageKey = imageColumn ? imageColumn.dataset.fallbackImageKey || '' : '';

			if (!accordionDetails.length) {
				return { addSwiper() {} };
			}

			function normalizeImageA11y() {
				if (!imageEls.length) return;
				imageEls.forEach((img) => {
					const isActive = img.classList.contains('is-active');
					img.setAttribute('aria-hidden', isActive ? 'false' : 'true');
				});
			}

			function showImageByKey(key) {
				if (!imageEls.length) return;
				let targetKey = key || '';

				if (targetKey) {
					const hasTarget = imageEls.some((img) => img.dataset.imageKey === targetKey);
					if (!hasTarget) targetKey = '';
				}
				if (!targetKey && fallbackImageKey) targetKey = fallbackImageKey;
				if (!targetKey) {
					const existingActive = imageEls.find((img) =>
						img.classList.contains('is-active')
					);
					targetKey = existingActive
						? existingActive.dataset.imageKey
						: imageEls[0].dataset.imageKey;
				}

				imageEls.forEach((img) => {
					const isActive = img.dataset.imageKey === targetKey;
					img.classList.toggle('is-active', isActive);
					img.setAttribute('aria-hidden', isActive ? 'false' : 'true');
				});
			}

			function showImageFromAccordion(item) {
				if (!item) return;
				const key = item.dataset.accordionImageKey || '';
				showImageByKey(key || fallbackImageKey);
			}

			function showImageFromActiveSlide() {
				if (!imageEls.length) return;
				let activeItem;
				if (swiper && swiper.slides && swiper.slides.length) {
					activeItem = swiper.slides[swiper.activeIndex];
				} else if (accordionSwiper) {
					activeItem = accordionSwiper.querySelector('.swiper-slide-active');
				}
				if (
					activeItem &&
					activeItem.classList &&
					activeItem.classList.contains('accordion-item')
				) {
					showImageFromAccordion(activeItem);
					return;
				}

				if (accordionDetails[0]) showImageFromAccordion(accordionDetails[0]);
				else showImageByKey(fallbackImageKey);
			}

			// Ensure initial state is consistent even if JS loads after paint.
			normalizeImageA11y();

			function ensureOneOpen(preferred) {
				const anyOpen = Array.from(accordionDetails).some((item) => item.open);
				if (anyOpen) return;

				const toOpen = preferred || accordionDetails[0];
				if (!toOpen) return;
				requestAnimationFrame(() => {
					toOpen.open = true;
				});
			}

			function setAllOpen(open) {
				accordionDetails.forEach((item) => {
					item.open = Boolean(open);
				});
			}

			function detailsClickBlocker(event) {
				event.preventDefault();
			}

			function disableDetailsGrouping() {
				accordionDetails.forEach((item) => {
					const name = item.getAttribute('name');
					if (name && !item.dataset.originalDetailsName) {
						item.dataset.originalDetailsName = name;
					}
					item.removeAttribute('name');
					item.addEventListener('click', detailsClickBlocker);
				});
			}

			function restoreDetailsGrouping() {
				accordionDetails.forEach((item) => {
					const original = item.dataset.originalDetailsName;
					if (original) item.setAttribute('name', original);
					item.removeEventListener('click', detailsClickBlocker);
				});
			}

			accordionDetails.forEach((item) => {
				const handler = () => {
					if (window.innerWidth <= 768) {
						// On mobile, show the image for the current slide.
						// If Swiper isn't initialized yet, use the toggled item.
						if (swiper && swiper.slides && swiper.slides.length) {
							const active = swiper.slides[swiper.activeIndex];
							if (active === item) showImageFromAccordion(item);
							else showImageFromActiveSlide();
						} else {
							showImageFromAccordion(item);
						}
						return;
					}

					if (item.open) {
						showImageFromAccordion(item);
						return;
					}

					// Keep one accordion open (exclusive group) and keep image in sync.
					const openItem = Array.from(accordionDetails).find((d) => d.open);
					if (openItem) {
						showImageFromAccordion(openItem);
					} else {
						ensureOneOpen(item);
						showImageByKey(fallbackImageKey);
					}
				};
				item.addEventListener('toggle', handler);
			});

			function addSwiper() {
				if (window.innerWidth <= 768) {
					// <details name="..."></details> enforces a single-open group.
					// Remove that grouping on mobile so we can open all items.
					disableDetailsGrouping();
					// Some browsers keep the "group" behavior for the current frame even after
					// removing the name attribute; opening in a loop then leaves only the last
					// item open. Defer to the next frame so the DOM attribute change is applied.
					if (openAllRaf) cancelAnimationFrame(openAllRaf);
					openAllRaf = requestAnimationFrame(() => setAllOpen(true));
					showImageFromActiveSlide();

					if (!init && accordionSwiper) {
						const paginationEl = accordionSwiper.querySelector('.swiper-pagination');
						swiper = new Swiper(accordionSwiper, {
							modules: [Navigation, Pagination],
							slidesPerView: 1,
							autoHeight: true,
							on: {
								init: () => showImageFromActiveSlide(),
								slideChange: () => showImageFromActiveSlide(),
							},
							pagination: {
								el: paginationEl,
								type: 'progressbar',
							},
							navigation: {
								nextEl: accordionSwiper.querySelector('.swiper-button-next'),
								prevEl: accordionSwiper.querySelector('.swiper-button-prev'),
							},
						});
						init = true;
						showImageFromActiveSlide();
					}
					if (init) showImageFromActiveSlide();
				} else {
					if (openAllRaf) cancelAnimationFrame(openAllRaf);
					openAllRaf = 0;

					if (init) {
						if (swiper) swiper.destroy(true, true);
						swiper = undefined;
						init = false;
					}

					restoreDetailsGrouping();
					setAllOpen(false);
					accordionDetails[0].open = true;
					showImageFromAccordion(accordionDetails[0]);
				}
			}

			return { addSwiper };
		}

		const instances = Array.from(sections).map((section) => initSection(section));
		function onResize() {
			instances.forEach((instance) => instance.addSwiper());
		}

		onResize();
		window.addEventListener('resize', onResize);
	},
};
